import { ISupportingDocument } from "../model/supporting.document.model";
import { IBusinessUnit } from "../model/businessUnit.model";
import { ShippingDetailsClass } from "./shippingDetail.model";
import {
  NewDecoration,
  NewPackSize,
  NewProduct,
  NewProductVariation,
} from "./ddb.model";
import { DecorationOption } from "./decoration.model";
import * as exactMath from "exact-math";
import { EventEmitter } from "@angular/core";
import { ICollection } from "./collection.model";
import { IStore, IStorePickup } from "./store.model";
import { IAuthUser, INewUser } from "./user.model";

export type OrderReceivedByUser = Partial<IAuthUser> & {
  first_name?: string;
  last_name?: string;
};

export class CartAttrs {
  id?: number;
  comment!: string | null;
  notes!: string | null;
  orderReference!: string | null;
  contactFirstName!: string | null;
  contactLastName!: string | null;
  contactEmail!: string | null;
  contactPhone!: string | null;
  businessUnitId!: number;
  orderNumber!: string;
  orderedAt!: string;
  sentForApprovalAt!: string | null;
  status!: string;
  statusLastChecked!: string | null;
  approvedById!: number | null;
  orderId!: number;
  trackingDetail!: string | null;
  trackingLink!: string | null;
  supportingDocuments!: ISupportingDocument[] | null;
  businessUnit!: IBusinessUnit | null;
  salesContactEmail!: string | null;
  salesContactName!: string | null;
  trackingDetailUrl!: string | null;
  grandTotal!: number;
  shippingDetails: ShippingDetailsClass = new ShippingDetailsClass();
  approvedBy?: { first_name: string; last_name: string; email: string };
  deletedByUser?: { firstName: string; lastName: string };
  /**
   * @description Checks if the cart has a comment attached to it
   */
  get hasComment(): boolean {
    return !!(this.comment && this.comment.length);
  }
  storeId?: string;
  order?: {
    storeId: string;
  };
  user?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  orderCreatedUser?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  customer?: {
    name: string;
  };
  pickup?: IStorePickup;
  store!: IStore;
  receivedBy?: string;
  receivedByUser?: OrderReceivedByUser;
  notifyRequestUser: boolean = false;

  items?: ServerCartItem[];
  placedById?: number;
  subTotal?: number;
  handlingTotal?: number;
  totalSubsidyAmount?: number;
  backOrderOption!: string | null;
  requiredDate?: string | null;
  rejectionReason?: string | null;
  shippingTotal?: number;
  taxTotal?: number;
  PlacedByUser?: INewUser;
}

export class ServerCartItem {
  id!: number;

  allocationCollectionId!: number | null;
  allocationCollectionConsumption!: number | null;
  allocationCollectionName!: string;
  allocationCollection!: {
    collectionId: string;
    collection: ICollection;
    name?: string;
  } | null;

  quantity!: number;
  product!: NewProduct;
  productId?: number;
  variation!: NewProductVariation;
  packSize?: NewPackSize;
  decorations?: {
    id: number;
    decorationId: number;
    decoration: NewDecoration;
    price?: number;
    options?: {
      decorationOption: DecorationOption;
      value: string;
    }[];
  }[];
  price?: number;
  comment!: string | null;
  deletedAt?: string | null;
  isRefund?: boolean;
  rejectQuanity?: number;

  asNewCartItem = (): NewCartItem => {
    const result = new NewCartItem(this.product, this);
    result.selectedQuantity = this.quantity;
    if (this.packSize) result.selectedPack = this.packSize;
    if (this.variation) {
      result.selectedVariation = this.variation;
    }

    result.selectedDecorations = [];
    if (this.decorations) {
      result.selectedDecorations = this.decorations.map(
        (orderItemDecoration) => orderItemDecoration.decoration,
      );
    }

    if (this.allocationCollectionId) {
      result.allocationCollectionId = this.allocationCollectionId;
      if (this.allocationCollectionConsumption)
        result.allocationCollectionConsumption =
          this.allocationCollectionConsumption;
    }

    return result;
  };
}

/**
 * @description Name should remain as NewCartItem as this is the client-side class which will handle the properties
 * required for building a CartItem for transmission
 */
export class NewCartItem {
  readonly product!: NewProduct;
  private _selectedColour!: string | null;
  public selectedColourString!: string | null;
  private _selectedSize!: string | null;
  public selectedSizeString!: string | null;
  private _selectedPack!: NewPackSize;
  private _selectedQuantity: number = 1;
  private _selectedVariation: NewProductVariation | undefined;

  public selectedDecorations: NewDecoration[] | undefined = [];

  quantityChanged: EventEmitter<NewCartItem> = new EventEmitter();

  /**
   * @description Allows for a newCartItem to push its quantity value back to the serverCartItem
   */
  public source!: ServerCartItem;
  notUsedAllocationIds?: number[];
  comment!: string | null;

  constructor(product?: NewProduct, source?: ServerCartItem) {
    if (product) this.product = product;
    if (source) this.source = source;
  }

  get totalQuantity() {
    if (!this.selectedQuantity) return 0;

    if (!this.selectedPack) return Number(this.selectedQuantity);

    return Number(this.selectedQuantity) * this.selectedPack.itemCount;
  }

  /**
   * @description Updates the selected quantity and creates events to notify subscribers the quantity changed
   *
   * @param val
   */
  set selectedQuantity(val: string | number) {
    let qty = val;
    if (typeof qty === "string") {
      qty = Number(qty.replace(/[^0-9]/, ""));
    } else if (val === 0) {
      qty = 1;
    }
    const newVal = Number.isNaN(qty) ? 1 : qty;
    const valChanged = this._selectedQuantity !== newVal;

    this._selectedQuantity = newVal;

    if (valChanged) {
      this.quantityChanged.emit(this);
    }
  }

  get selectedQuantity(): number | string {
    return this._selectedQuantity;
  }

  /**
   * @description Sets the selected packet (size)
   *
   * @param val
   */
  set selectedPack(val: NewPackSize) {
    this._selectedPack = val;
  }

  /**
   * @description Gets the selected packet (size)
   *
   * @returns {NewPackSize|null}
   */
  get selectedPack() {
    return this._selectedPack;
  }

  /**
   * @description Sets the selected colour in lowercase
   *
   * @param val
   */
  set selectedColour(val: string | null) {
    this._selectedColour = val ? val.toLowerCase() : val;
    this.selectedColourString = val;
  }

  /**
   * @description Gets the current lowercase selected colour or null
   *
   * @returns {string|null}
   */
  get selectedColour(): string | null {
    return this._selectedColour;
  }

  /**
   * @description sets the selected size in lower case
   *
   * @param val
   */
  set selectedSize(val: string | null) {
    this._selectedSize = val ? val.toLowerCase() : val;
    this.selectedSizeString = val;
  }

  /**
   * @description Gets the current lowercase selected size or null
   *
   * @returns {string|null}
   */
  get selectedSize(): string | null {
    return this._selectedSize;
  }

  /**
   * @description Recalculates the selected variation on get. No set is exposed to ensure the cart Item cannot be
   * forcefully updated
   *
   * @returns {NewProductVariation}
   */
  get selectedVariation(): NewProductVariation | undefined {
    if (!this.hasVariations()) return this._selectedVariation;

    return this.product.variations.find(
      (variation) =>
        (variation.colour || "").toLowerCase() ===
        (this.selectedColour || "") &&
        (variation.size || "").toString().toLowerCase() ===
        (this.selectedSize || ""),
    );
  }

  /**
   * @description Manually sets the selected variation to the specified value
   *
   * @param val
   */
  set selectedVariation(val: NewProductVariation | undefined) {
    this._selectedVariation = val;
    if (val) {
      this.selectedColour = val.colour;
      this.selectedSize =
        val.size !== null && val.size !== undefined
          ? val.size.toString()
          : null;
    }
  }

  /**
   * @description Centralized method for determining if the current cartItem has a product and variations it can process
   */
  private hasVariations = () =>
    this.product && this.product.variations && this.product.variations.length;

  /**
   * @description Modifies the quantity of items selected. This intentionally forces cooersion so that any form input
   * which changed this value to a string has no effect
   *
   * @param {number} mod A positive or negative integer which should be added to the selected quantity
   */
  public incrementQuantity = (mod: number) => {
    this.selectedQuantity = Number(this.selectedQuantity) + mod;

    if (this.selectedQuantity < 1) this.selectedQuantity = 1;
  };

  public isVarientInStock(
    storeId: string | null,
    cartItems: ServerCartItem[] = [],
    variantId = this.selectedVariation?.id,
  ): boolean {
    if (!storeId) return true;

    const availableStock = this.getAvailableStock(variantId);
    const selectedQty = Number(this.selectedQuantity) || 0;

    const existingCartQty =
      cartItems
        ?.filter((item) => item.variation?.id === variantId)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0) || 0;

    const totalRequested = existingCartQty + selectedQty;

    return totalRequested <= availableStock;
  }

  public getAvailableStock(variantId = this.selectedVariation?.id): number {
    if (!variantId || !this.product?.variations?.length) return 0;

    const variation = this.product.variations.find((v) => v.id === variantId);

    if (!variation) return 0;

    const purchaseQuantity = variation.purchaseQuantity ?? 0;
    const soldQuantity = variation.soldQuantity ?? 0;

    return Math.max(purchaseQuantity - soldQuantity, 0);
  }

  public canIncreaseQuantity(
    storeId: string | null,
    variantId = this.selectedVariation?.id,
  ): boolean {
    if (!storeId) return true;

    const availableStock = this.getAvailableStock(variantId);
    const selectedQty = Number(this.selectedQuantity) || 0;

    return selectedQty < availableStock;
  }

  /**
   * @description Returns a list of each unique size offered on the product attached to the cartItem
   *
   * @returns {Array<string>}
   */
  public getUniqueSizes(): Array<string | number> | undefined {
    if (!this.hasVariations()) return undefined;

    const result = this.product.variations
      .map((variation) => variation.size)
      .filter((val, idx, self) => val !== null && self.indexOf(val) === idx);

    if (
      result.length > 1 ||
      (result.length === 1 && result[0].toString().length > 0)
    )
      return result;

    return undefined;
  }

  /**
   * @description Returns a list of each unique colours offered on the product attached to the cartItem
   *
   * @returns {Array<string>}
   */
  public getUniqueColours(checkResultLength = true): Array<string> | undefined {
    if (!this.hasVariations()) return undefined;

    const result = this.product.variations
      .map((variation) => variation.colour)
      .filter((val, idx, self) => val !== null && self.indexOf(val) === idx);

    if (
      result.length > 1 ||
      !checkResultLength ||
      (result.length === 1 && result[0].toString().length > 0)
    )
      return result;

    return undefined;
  }

  public getUnitPrice(ignoreAllocation: boolean): number | null {
    let count = exactMath.mul(
      Number(this.selectedQuantity),
      this.selectedPack ? this.selectedPack.itemCount : 1,
    );

    if (!ignoreAllocation && this.allocationCollectionConsumption) {
      count = count - this.allocationCollectionConsumption;
    }

    if (count === 0) {
      return 0;
    }

    const total = this.getTotalPrice(ignoreAllocation);

    if (total === null || total === undefined) return null;

    return exactMath.div(total, count);
  }

  public getTotalQuantity(): number {
    return exactMath.mul(
      Number(this.selectedQuantity),
      this.selectedPack ? this.selectedPack.itemCount : 1,
    );
  }

  public isFullyAllocated(): boolean {
    return (
      this.getTotalQuantity() <= (this.allocationCollectionConsumption || 0)
    );
  }

  /**
   * @description estimates the total expected price for this cart item
   *
   * @returns {number|null}
   */
  public getTotalPrice(ignoreAllocation: boolean = false): number {
    if (!this.selectedQuantity || !this.product) return 0;

    const price = this.product.price || 0;
    const finalPrice = exactMath.sub(price, this.product.subsidyAmount || 0);

    let itemPrice = exactMath.mul(this.getTotalQuantity(), finalPrice);

    let decorationPrice = 0;

    if (this.selectedDecorations) {
      decorationPrice = exactMath.mul(
        Number(this.selectedQuantity),
        this.selectedPack ? this.selectedPack.itemCount : 1,
        this.selectedDecorations.reduce(
          (accumulator, val) =>
            exactMath.add(
              accumulator,
              exactMath.sub(val.price || 0, val.subsidyAmount || 0) || 0,
            ),
          0,
        ),
      );
    }

    if (!ignoreAllocation && this.allocationCollectionConsumption) {
      itemPrice -= exactMath.mul(
        Number(this.allocationCollectionConsumption),
        this.selectedPack ? this.selectedPack.itemCount : 1,
        finalPrice,
      );

      if (this.selectedDecorations) {
        decorationPrice -= exactMath.mul(
          Number(this.allocationCollectionConsumption),
          this.selectedPack ? this.selectedPack.itemCount : 1,
          this.selectedDecorations.reduce(
            (accumulator, val) =>
              exactMath.add(
                accumulator,
                exactMath.sub(val.price || 0, val.subsidyAmount || 0),
              ),
            0,
          ),
        );
      }
    }

    return Math.max(0, exactMath.add(itemPrice, decorationPrice));
  }

  /**
   * @description estimates the total subsidy amount for this cart item
   *
   * @returns {number|null}
   */
  public getTotalSubsidy(ignoreAllocation: boolean = false): number {
    if (!this.selectedQuantity || !this.product) return 0;

    const subsidyAmount = this.product.subsidyAmount || 0;

    let totalSubsidy = exactMath.mul(this.getTotalQuantity(), subsidyAmount);

    // Include decoration subsidies if needed
    if (this.selectedDecorations) {
      totalSubsidy = exactMath.add(
        totalSubsidy,
        exactMath.mul(
          Number(this.selectedQuantity),
          this.selectedPack ? this.selectedPack.itemCount : 1,
          this.selectedDecorations.reduce(
            (acc, val) => exactMath.add(acc, val.subsidyAmount || 0),
            0,
          ),
        ),
      );
    }

    if (!ignoreAllocation && this.allocationCollectionConsumption) {
      totalSubsidy = exactMath.sub(
        totalSubsidy,
        exactMath.mul(
          Number(this.allocationCollectionConsumption),
          this.selectedPack ? this.selectedPack.itemCount : 1,
          subsidyAmount,
        ),
      );

      if (this.selectedDecorations) {
        totalSubsidy = exactMath.sub(
          totalSubsidy,
          exactMath.mul(
            Number(this.allocationCollectionConsumption),
            this.selectedPack ? this.selectedPack.itemCount : 1,
            this.selectedDecorations.reduce(
              (acc, val) => exactMath.add(acc, val.subsidyAmount || 0),
              0,
            ),
          ),
        );
      }
    }

    return Math.max(0, totalSubsidy);
  }

  /**
   * Estimates the total expected cost to the account for this cart item
   */
  public getAccountPrice(): number {
    if (!this.selectedQuantity || !this.product) return 0;

    const itemQty =
      Number(this.selectedQuantity) -
      Number(this.allocationCollectionConsumption || 0);

    let itemPrice = exactMath.mul(
      itemQty,
      this.selectedPack ? this.selectedPack.itemCount : 1,
      Math.min(this.product.subsidyAmount || 0, this.product.price || 0),
    );

    return itemPrice;
  }

  /**
   * Estimates the total expected cost to the account for this cart item
   */
  public getUserPrice(): number {
    return exactMath.sub(this.getTotalPrice(), this.getAccountPrice());
  }

  /**
   * @description Pushes the currently selected quantity back up to the source server item if applicable
   */
  public pushQuantity() {
    if (!this.source) return;

    this.source.quantity = Number(this.selectedQuantity);
  }

  public allocationCollectionId: number | undefined;
  public allocationCollectionConsumption: number | undefined;
}

export enum CartStatus {
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  BACKORDERED = "BACKORDERED",
  APPROVAL = "APPROVAL",
  PENDING = "PENDING",
}

export class Cart {
  id!: number;

  attrs: CartAttrs = new CartAttrs();

  itemCount: number = 0;

  items?: ServerCartItem[];

  storeId?: number;
}

export interface OrderTransaction {
  consumedAllocation?: number;
  EPS_AMOUNT?: number | string;
  responseMessage?: string;
  responseTimeStamp?: string;
  refundAmount?: number;
  refundId?: string;
  refundStatus?: string;
}

export interface OrderFieldValue {
  customerUserField?: {
    fieldName: string;
  };
  fieldValue: string;
}

/** Full order aggregate returned by GET /cart/:id. */
export interface Order {
  id: number;
  attrs: CartAttrs;
  items: ServerCartItem[];
  storeId?: number;
  customerId: string;
  actualLength?: number;
  customer?: {
    handlingFee?: number;
    handlingFeeThreshold?: number;
    isFlatRateShippingEnabled?: boolean;
    poLabelName?: string;
  };
  customerUser?: {
    approvalLimit?: number;
  };
  orderFieldValue: OrderFieldValue[];
  store?: IStore;
  transaction?: OrderTransaction;
  user: INewUser & {
    contactNumber?: string;
  };
}

export interface IStatusStats {
  orders: number;
  value: number;
}

export interface IDayStats {
  totalOrders: number;
  totalValue: number;
  status: {
    [key: string]: IStatusStats;
  };
}

export interface IWeeklyChart {
  Sun: IDayStats;
  Mon: IDayStats;
  Tue: IDayStats;
  Wed: IDayStats;
  Thu: IDayStats;
  Fri: IDayStats;
  Sat: IDayStats;
}

export interface IMonthlyWeek {
  week1: IDayStats;
  week2: IDayStats;
  week3: IDayStats;
  week4: IDayStats;
  week5: IDayStats;
}

export interface IYesterdayStats extends IDayStats { }

export interface IOrderChartResponse {
  weekly: IWeeklyChart;
  monthly: IMonthlyWeek;
  yesterday: IYesterdayStats;
}

// Core business unit
export interface IBusinessUnitChart {
  id: number;
  name: string;
  code: string;
}

// Order/value info per business unit
export interface IBusinessUnitChartItem {
  totalOrders: number;
  totalValue: number;
  businessUnit: IBusinessUnitChart;
}

// Weekly breakdown
export interface IBusinessUnitChartWeekly {
  Sun: IBusinessUnitChartItem[];
  Mon: IBusinessUnitChartItem[];
  Tue: IBusinessUnitChartItem[];
  Wed: IBusinessUnitChartItem[];
  Thu: IBusinessUnitChartItem[];
  Fri: IBusinessUnitChartItem[];
  Sat: IBusinessUnitChartItem[];
}

// Monthly breakdown
export interface IBusinessUnitChartMonthly {
  week1: IBusinessUnitChartItem[];
  week2: IBusinessUnitChartItem[];
  week3: IBusinessUnitChartItem[];
  week4: IBusinessUnitChartItem[];
  week5: IBusinessUnitChartItem[];
}

// Yesterday data
export interface IBusinessUnitChartYesterday {
  totalOrders: number;
  totalValue: number;
  businessUnit: IBusinessUnitChart;
}

// Root response
export interface IBusinessUnitChartResponse {
  weekly: IBusinessUnitChartWeekly;
  monthly: IBusinessUnitChartMonthly;
  yesterday: IBusinessUnitChartYesterday[];
}

export interface IVisitorMetric {
  label: string;
  value: number;
}

export interface IVisitorResponse {
  weekly: IVisitorMetric[];
  monthly: IVisitorMetric[];
  yearly: IVisitorMetric[];
}

export interface ICartList {
  approvalOrder: number;
  backOrders: number;
  completedOrders: number;
  count: number;
  todayOrder: number;
  totalOrders: number;
  rows: CartAttrs[]
}
